/**
 * Données des deux onglets graphiques, calculées à partir du résultat du modèle.
 * Pur TypeScript, testé. Le rendu SVG est dans Profil.tsx et EspaceTemps.tsx.
 */
import type { RouteData } from "../model/route";
import type { RunResult } from "../model/run";

export interface RoadLeg {
  from: string;
  to: string;
  km: number;
  creuse_min: number;
  pointe_min: number;
  pointe_source: string;
}

export interface RoadTimes {
  source_creuse: string;
  source_pointe: string;
  legs: RoadLeg[];
}

export type Pt = [number, number];

/** Pas de sous-échantillonnage pour tenir ~1 000 points quelle que soit la largeur. */
export function sampleStep(n: number, target = 1200): number {
  return Math.max(1, Math.floor(n / target));
}

/** Profil : pour chaque point échantillonné, PK (km), altitude terrain, altitude rail, vitesse (km/h), limite (km/h). */
export interface ProfilSeries {
  pk: number[];
  terrain: number[];
  rail: number[];
  v: number[];
  vmax: number[];
}

export function profilSeries(route: RouteData, result: RunResult | null, step = sampleStep(route.coords.length)): ProfilSeries {
  const n = route.coords.length;
  const out: ProfilSeries = { pk: [], terrain: [], rail: [], v: [], vmax: [] };
  const push = (i: number) => {
    out.pk.push((i * route.step_m) / 1000);
    out.terrain.push(route.z_terrain[i]);
    out.rail.push(route.z_rail[i]);
    out.v.push(result ? result.v[i] * 3.6 : 0);
    out.vmax.push(route.vmax_kmh[i]);
  };
  for (let i = 0; i < n; i += step) push(i);
  if ((n - 1) % step !== 0) push(n - 1);
  // les arrêts eux-mêmes, pour que les creux de vitesse touchent bien zéro
  if (result) {
    for (const s of result.stops) {
      if (s % step !== 0) {
        const k = out.pk.findIndex((p) => p > (s * route.step_m) / 1000);
        const at = k < 0 ? out.pk.length : k;
        out.pk.splice(at, 0, (s * route.step_m) / 1000);
        out.terrain.splice(at, 0, route.z_terrain[s]);
        out.rail.splice(at, 0, route.z_rail[s]);
        out.v.splice(at, 0, 0);
        out.vmax.splice(at, 0, route.vmax_kmh[s]);
      }
    }
  }
  return out;
}

/** Espace-temps : la marche d'un scénario en (PK km, minutes depuis le départ), avec les arrêts en gare (segments verticaux). */
export function mareyLine(route: RouteData, result: RunResult, step = sampleStep(route.coords.length)): Pt[] {
  const { stops, tCum, dwell_s } = result;
  if (stops.length < 2) return [];
  const pts: Pt[] = [];
  const first = stops[0];
  let offset = -tCum[first];
  const km = (i: number) => (i * route.step_m) / 1000;
  for (let k = 0; k < stops.length - 1; k++) {
    const a = stops[k];
    const b = stops[k + 1];
    for (let i = a; i < b; i += step) pts.push([km(i), (tCum[i] + offset) / 60]);
    pts.push([km(b), (tCum[b] + offset) / 60]);
    if (k < stops.length - 2) {
      offset += dwell_s;
      pts.push([km(b), (tCum[b] + offset) / 60]);
    }
  }
  return pts;
}

/** Voiture : deux lignes brisées (heure creuse, heure de pointe) posées sur les PK des repères. */
export function carLines(route: RouteData, roads: RoadTimes): { creuse: Pt[]; pointe: Pt[]; totalCreuse: number; totalPointe: number } | null {
  const lm = route.landmarks;
  if (lm.length < 2) return null;
  const km = (i: number) => (i * route.step_m) / 1000;
  const creuse: Pt[] = [[km(lm[0].idx), 0]];
  const pointe: Pt[] = [[km(lm[0].idx), 0]];
  let tc = 0;
  let tp = 0;
  for (let k = 0; k < lm.length - 1; k++) {
    const leg = roads.legs.find((l) => l.from === lm[k].name && l.to === lm[k + 1].name);
    if (!leg) return null;
    tc += leg.creuse_min;
    tp += leg.pointe_min;
    creuse.push([km(lm[k + 1].idx), tc]);
    pointe.push([km(lm[k + 1].idx), tp]);
  }
  return { creuse, pointe, totalCreuse: tc, totalPointe: tp };
}

/** Interpolation linéaire d'une ligne brisée triée en x. */
export function interpAt(line: Pt[], x: number): number | null {
  if (line.length === 0 || x < line[0][0] || x > line[line.length - 1][0]) return null;
  let lo = 0;
  let hi = line.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (line[mid][0] <= x) lo = mid;
    else hi = mid;
  }
  const [x0, y0] = line[lo];
  const [x1, y1] = line[hi];
  if (x1 === x0) return Math.max(y0, y1);
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}

/** Graduations « propres » pour un axe. */
export function ticks(min: number, max: number, count = 5): number[] {
  const span = max - min;
  if (span <= 0) return [min];
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const stepN = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  const step = stepN * mag;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

/** Graduations de temps : multiples de 15 / 30 / 60 min. */
export function timeTicks(maxMin: number): number[] {
  const step = maxMin <= 60 ? 15 : maxMin <= 150 ? 30 : 60;
  const out: number[] = [];
  for (let v = 0; v <= maxMin; v += step) out.push(v);
  return out;
}

export function fmtMin(min: number): string {
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h} h` : `${h} h ${String(r).padStart(2, "0")}`;
}
